import * as spec from '@galacean/effects-specification';
import type {
  BaseContent, BinaryFile, CompositionData, Item, JSONScene, JSONSceneLegacy, SpineResource,
  SpineContent, TimelineAssetData, CustomShapeData, ShapeComponentData, CompositionContent,
} from '@galacean/effects-specification';
import {
  DataType, END_BEHAVIOR_PAUSE, END_BEHAVIOR_PAUSE_AND_DESTROY, EndBehavior, ItemType,
  JSONSceneVersion, ShapePrimitiveType,
} from '@galacean/effects-specification';
import { MaskMode } from '../material';
import { generateGUID } from '../utils';
import { convertAnchor, ensureFixedNumber, ensureFixedVec3 } from './utils';
import { getGeometryByShape } from '../shape/geometry';

let currentMaskComponent: string;
const componentMap: Map<string, spec.ComponentData> = new Map();
const itemMap: Map<string, spec.VFXItemData> = new Map();

/**
 * 2.1 以下版本数据适配（mars-player@2.4.0 及以上版本支持 2.1 以下数据的适配）
 */
export function version21Migration (json: JSONSceneLegacy): JSONSceneLegacy {
  json.compositions.forEach(composition => {
    composition.items.forEach(item => {
      if (item.type === ItemType.null) {
        if (item.endBehavior === EndBehavior.destroy) {
          item.endBehavior = EndBehavior.freeze;
        }
      }
    });
  });

  json.version = JSONSceneVersion['2_1'];

  return json;
}

/**
 * 2.2 以下版本数据适配（mars-player@2.5.0 及以上版本支持 2.2 以下数据的适配）
 */
export function version22Migration (json: JSONSceneLegacy): JSONSceneLegacy {
  const singleVersion = json.version?.split('.');

  if (!singleVersion || Number(singleVersion[0]) > 2 || (Number(singleVersion[0]) === 2 && Number(singleVersion[1]) >= 2)) {
    return json;
  }

  json.compositions.forEach(composition => {
    composition.items.forEach(item => {
      if (item.type === ItemType.mesh || item.type === ItemType.light) {
        item.endBehavior = item.endBehavior as unknown === 1 ? EndBehavior.destroy : item.endBehavior;
      }
    });
  });

  return json;
}

/**
 * 2.5 以下版本 赫尔米特数据转换成贝塞尔数据
 */
export function version24Migration (json: JSONScene): JSONScene {
  // 曲线转换成贝塞尔
  json.compositions.map((comp: any) => {
    for (const item of comp.items) {
      convertParam(item.content);
    }
  });

  return json;
}

/**
 * 3.0 以下版本数据适配（runtime 2.0及以上版本支持）
 */
export function version30Migration (json: JSONSceneLegacy): JSONScene {
  const result: JSONScene = {
    ...json,
    items: [],
    compositions: [],
    components: [],
    materials: [],
    shaders: [],
    geometries: [],
    animations: [],
    miscs: [],
  };

  // image数据添加 guid
  for (const image of result.images) {
    image.id = generateGUID();
  }

  // 兼容老版本数据中不存在textures的情况
  result.textures ??= [];
  result.textures.forEach(textureOptions => {
    textureOptions.id = generateGUID();
    textureOptions.dataType = DataType.Texture;
    // @ts-expect-error
    textureOptions.source = { id: result.images[textureOptions.source]?.id };
  });

  if (result.textures.length < result.images.length) {
    for (let i = result.textures.length; i < result.images.length; i++) {
      result.textures.push({
        id: generateGUID(),
        dataType: DataType.Texture,
        source: { id: result.images[i].id },
        flipY: true,
      });
    }
  }

  // 处理老版本数据中 bins 没有 id 的情况
  if (json.bins) {
    convertBinaryAsset(json.bins, result);
  }

  const itemOldIdToGuidMap: Record<string, string> = {};
  const guidToItemMap: Record<string, Item> = {};

  // 更正Composition.endBehavior
  for (const composition of json.compositions) {
    // composition 的 endBehavior 兼容
    if (
      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      composition.endBehavior === END_BEHAVIOR_PAUSE_AND_DESTROY ||
      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      composition.endBehavior === END_BEHAVIOR_PAUSE
    ) {
      composition.endBehavior = EndBehavior.freeze;
    }

    // 过滤掉滤镜元素
    composition.items = composition.items.filter(item => item.type !== '8' as ItemType);

    // 过滤掉粒子滤镜（扭曲）
    composition.items.forEach(item => {
      if (item.type === ItemType.particle) {
        // @ts-expect-error
        const filterData = item.content['filter'];

        if (filterData) {
          // @ts-expect-error
          delete item.content['filter'];
        }
      }
    });

    for (const item of composition.items) {
      itemOldIdToGuidMap[item.id] = generateGUID();
      // TODO: 编辑器测试用，上线后删除
      //@ts-expect-error
      item.oldId = item.id;
      item.id = itemOldIdToGuidMap[item.id];
      guidToItemMap[item.id] = item;
    }

    composition.items.forEach((item, index) => {
      if (item.parentId) {
        if (item.parentId.includes('^')) {
          const parentId = (item.parentId).split('^')[0];
          const nodeId = (item.parentId).split('^')[1];

          item.parentId = itemOldIdToGuidMap[parentId] + '^' + nodeId;
        } else {
          item.parentId = itemOldIdToGuidMap[item.parentId];
        }
      }

      // @ts-expect-error fix item type
      result.items.push(item);

      // @ts-expect-error fix item type
      composition.items[index] = { id: item.id };
    });

    const compositionData: CompositionData = {
      ...composition,
      //@ts-expect-error
      timelineAsset: { id: '' },
      sceneBindings: [],
    };

    result.compositions.push(compositionData);
    // 生成时间轴数据
    convertTimelineAsset(compositionData, guidToItemMap, result);

  }

  for (const item of result.items) {
    // 原 texture 索引转为统一 guid 索引
    if (item.content) {
      if (item.content.renderer) {
        if (item.content.renderer.texture !== undefined) {
          const oldTextureId = item.content.renderer.texture;

          item.content.renderer.texture = { id: result.textures[oldTextureId].id };
        }
      }

      if (item.content.trails) {
        if (item.content.trails.texture !== undefined) {
          const oldTextureId = item.content.trails.texture;

          item.content.trails.texture = { id: result.textures[oldTextureId].id };
        }
      }
    }

    // item 的 transform 属性由数组转为 {x:n, y:n, z:n}
    if (item.transform) {
      //@ts-expect-error
      const position = [...item.transform.position ?? [0, 0, 0]];
      //@ts-expect-error
      const rotation = [...item.transform.rotation ?? [0, 0, 0]] as number[];
      //@ts-expect-error
      const scale = [...item.transform.scale ?? [1, 1, 1]];

      Object.assign(item, {
        transform: {
          position: { x: position[0], y: position[1], z: position[2] },
          eulerHint: { x: rotation[0], y: rotation[1], z: rotation[2] },
          scale: { x: scale[0], y: scale[1], z: scale[0] },
        },
      });

      // sprite 的 scale 转为 size
      if (item.type === ItemType.sprite) {
        item.transform.size = { x: scale[0], y: scale[1] };
        item.transform.scale = { x: 1, y: 1, z: 1 };
      }

      // sprite 的 anchor 修正
      if (item.type === ItemType.sprite) {
        const content = item.content;

        if (!content.renderer) {
          content.renderer = {};
        }
        const renderer = content.renderer;
        const realAnchor = convertAnchor(renderer.anchor, renderer.particleOrigin);
        const startSize = item.transform.size;

        // 兼容旧JSON（anchor和particleOrigin可能同时存在）
        if (!renderer.anchor && renderer.particleOrigin !== undefined) {
          item.transform.position.x += -realAnchor[0] * (startSize?.x ?? 1);
          item.transform.position.y += -realAnchor[1] * (startSize?.y ?? 1);
        }
        item.transform.anchor = { x: realAnchor[0] * (startSize?.x ?? 1), y: realAnchor[1] * (startSize?.y ?? 1) };
      }
    }

    if (item.type === ItemType.particle) {
      const content = item.content;

      if (!content.renderer) {
        content.renderer = {};
      }
      const renderer = content.renderer;

      content.renderer.anchor = convertAnchor(renderer.anchor, renderer.particleOrigin);
    }

    // 修复相机K帧缺失 asMovement 参数
    if (item.type === ItemType.camera && item.content.positionOverLifetime && Object.keys(item.content.positionOverLifetime).length !== 0) {
      item.content.positionOverLifetime.asMovement = true;
    }

    // 修正老 json 的 item.pluginName
    if (item.pn !== undefined) {
      const pn = item.pn;
      const { plugins = [] } = json;

      if (pn !== undefined && Number.isInteger(pn)) {
        item.pluginName = plugins[pn];
      }
    }

    // 修正老 json 的 item.type
    if (item.pluginName === 'editor-gizmo') {
      //@ts-expect-error
      item.type = 'editor-gizmo';
    }
    if (item.pluginName === 'orientation-transformer') {
      //@ts-expect-error
      item.type = 'orientation-transformer';
    }

    // gizmo 的 target id 转换为新的 item guid
    if (item.content.options.target && item.pluginName === 'editor-gizmo') {
      item.content.options.target = itemOldIdToGuidMap[item.content.options.target];
    }

    // Spine 元素转为 guid 索引
    if (
      item.type === ItemType.spine
      && json.spines
      && json.spines.length !== 0
    ) {
      convertSpineData(json.spines[item.content.options.spine], item.content, result);
    }

    // item 的 content 转为 component data 加入 JSONScene.components
    if (
      item.type === ItemType.sprite ||
      item.type === ItemType.particle ||
      item.type === ItemType.mesh ||
      item.type === ItemType.skybox ||
      item.type === ItemType.light ||
      item.type === 'camera' as ItemType ||
      item.type === ItemType.tree ||
      item.type === ItemType.interact ||
      item.type === ItemType.camera ||
      item.type === ItemType.text ||
      item.type === ItemType.spine ||
      item.type === 'editor-gizmo' as ItemType ||
      item.type === 'orientation-transformer' as ItemType
    ) {
      item.components = [];
      result.components.push(item.content);
      item.content.id = generateGUID();
      item.content.item = { id: item.id };
      item.dataType = DataType.VFXItemData;
      item.components.push({ id: item.content.id });
    }

    if (item.type === ItemType.null || item.type === ItemType.composition) {
      item.components = [];
      item.dataType = DataType.VFXItemData;
    }

    switch (item.type) {
      case ItemType.sprite:
        item.content.dataType = DataType.SpriteComponent;

        break;
      case ItemType.particle:
        item.content.dataType = DataType.ParticleSystem;

        break;
      case ItemType.mesh:
        item.content.dataType = DataType.MeshComponent;

        break;
      case ItemType.skybox:
        item.content.dataType = DataType.SkyboxComponent;

        break;
      case ItemType.light:
        item.content.dataType = DataType.LightComponent;

        break;
      case 'camera' as ItemType:
        item.content.dataType = DataType.CameraComponent;

        break;
      case 'editor-gizmo' as ItemType:
        item.content.dataType = 'GizmoComponent';

        break;
      case 'orientation-transformer' as ItemType:
        item.content.dataType = 'OrientationComponent';

        break;
      case ItemType.tree:
        item.content.dataType = DataType.TreeComponent;

        break;
      case ItemType.interact:
        item.content.dataType = DataType.InteractComponent;

        break;
      case ItemType.camera:
        item.content.dataType = DataType.CameraController;

        break;
      case ItemType.text:
        item.content.dataType = DataType.TextComponent;

        break;
      case ItemType.spine:
        item.content.dataType = DataType.SpineComponent;

        break;
    }
  }

  result.version = JSONSceneVersion['3_0'];

  return result;
}

/**
 * 3.1 版本数据适配
 * - 富文本插件名称的适配
 */
export function version31Migration (json: JSONScene): JSONScene {
  // Custom shape fill 属性位置迁移
  for (const component of json.components) {
    if (component.dataType === DataType.ShapeComponent) {
      const shapeComponent = component as ShapeComponentData;

      if (shapeComponent.type === ShapePrimitiveType.Custom) {
        const customShapeComponent = shapeComponent as CustomShapeData;

        //@ts-expect-error
        if (customShapeComponent.shapes?.length > 0 && customShapeComponent.shapes[0].fill) {
          // @ts-expect-error
          customShapeComponent.fill = customShapeComponent.shapes[0].fill;
        }

        // easingIn 和 easingOut 绝对坐标转相对坐标
        const easingInFlag = new Array(customShapeComponent.easingIns.length);
        const easingOutFlag = new Array(customShapeComponent.easingOuts.length).fill(false);

        for (const shape of customShapeComponent.shapes) {
          for (const index of shape.indexes) {
            const point = customShapeComponent.points[index.point];
            const easingIn = customShapeComponent.easingIns[index.easingIn];
            const easingOut = customShapeComponent.easingOuts[index.easingOut];

            if (!easingInFlag[index.easingIn]) {
              easingIn.x -= point.x;
              easingIn.y -= point.y;
              easingInFlag[index.easingIn] = true;
            }
            if (!easingOutFlag[index.easingOut]) {
              easingOut.x -= point.x;
              easingOut.y -= point.y;
              easingOutFlag[index.easingOut] = true;
            }
          }
        }
      }
    }
  }

  json.version = JSONSceneVersion['3_2'];

  return json;
}

export function version32Migration (json: JSONScene): JSONScene {
  componentMap.clear();
  itemMap.clear();

  const { compositions, items, components } = json;
  // 处理旧蒙版数据

  for (const component of components) {
    componentMap.set(component.id, component);
  }
  for (const item of items) {
    itemMap.set(item.id, item);
  }

  for (const comp of compositions) {
    processContent(comp);
  }

  json.version = JSONSceneVersion['3_3'];

  return json;
}

export function version33Migration (json: JSONScene): JSONScene {
  // 修正老版本数据中，富文本插件名称的问题
  json.plugins?.forEach((plugin, index) => {
    if (plugin === 'richtext') {
      json.plugins[index] = 'rich-text';
    }
  });

  // 老 shape 数据兼容
  for (const item of json.items) {
    if (item.type === spec.ItemType.sprite) {
      const spriteComponent = componentMap.get(item.components[0].id) as spec.SpriteComponentData;

      if (spriteComponent) {
        const shape = spriteComponent.renderer.shape;
        let shapeData;

        if (Number.isInteger(shape)) {
          // @ts-expect-error
          shapeData = json.shapes?.[shape as number];
        } else {
          shapeData = shape;
        }

        spriteComponent.renderer.shape = shapeData;
      }
    }
  }

  // Composition id 转 guid, Composition 分离 CompositionComponent
  const compositionId = json.compositionId;
  const compositionIdToGUIDMap: Record<string, string> = {};

  for (const composition of json.compositions) {
    const guid = generateGUID();

    compositionIdToGUIDMap[composition.id] = guid;
    if (composition.id === compositionId) {
      json.compositionId = guid;
    }
    composition.id = guid;

    const compositionComponent = {
      id: generateGUID(),
      dataType: 'CompositionComponent',
      //@ts-expect-error
      items: composition.items,
      //@ts-expect-error
      timelineAsset: composition.timelineAsset,
      //@ts-expect-error
      sceneBindings: composition.sceneBindings,
      item: { id: composition.id },
    } as unknown as spec.ComponentData;

    //@ts-expect-error
    composition.timelineAsset = undefined;
    //@ts-expect-error
    composition.sceneBindings = undefined;
    composition.components = [{ id: compositionComponent.id }];
    json.components.push(compositionComponent);
  }
  // 预合成元素 refId 同步改为生成的合成 guid
  for (const item of json.items) {
    if (item.content) {
      const compositionOptions = (item.content as CompositionContent).options;

      if (compositionOptions && compositionOptions.refId !== undefined) {
        compositionOptions.refId = compositionIdToGUIDMap[compositionOptions.refId];
      }
    }
  }

  json.version = JSONSceneVersion['3_4'];

  return json;
}

export function version34Migration (json: JSONScene): JSONScene {
  const idToComponentMap: Record<string, spec.ComponentData> = {};

  for (const componentData of json.components) {
    idToComponentMap[componentData.id] = componentData;
  }

  // 修复合成组件的 item id 问题
  for (const composition of json.compositions) {
    for (const component of composition.components) {
      const componentID = (component).id;

      idToComponentMap[componentID].item.id = composition.id;
    }
  }

  // 兼容老 Shape 资源
  for (const componentData of json.components) {
    if (componentData.dataType === spec.DataType.SpriteComponent) {
      const spriteComponentData = componentData as spec.SpriteComponentData;
      const renderer = spriteComponentData.renderer;
      const shapeData = renderer.shape as spec.ShapeGeometry;

      if (shapeData !== undefined && shapeData !== null && !('aPoint' in shapeData && 'index' in shapeData)) {
        const geometryData = createGeometryDataByShape(shapeData);

        spriteComponentData.geometry = { id: geometryData.id };
        json.geometries.push(geometryData);
      }
    }

    if (componentData.dataType === spec.DataType.ShapeComponent) {
      const shapeComponentData = componentData as ShapeComponentData;

      shapeComponentData.fills = [];
      //@ts-expect-error
      if (shapeComponentData.fill) {
        const solidPaintData: spec.SolidPaintData = {
          type: spec.FillType.Solid,
          //@ts-expect-error
          color: shapeComponentData.fill.color,
        };

        shapeComponentData.fills.push(solidPaintData);
      }
      //@ts-expect-error
      delete shapeComponentData.fill;

      shapeComponentData.strokes = [];
      //@ts-expect-error
      if (shapeComponentData.stroke) {
        const solidPaintData: spec.SolidPaintData = {
          type: spec.FillType.Solid,
          //@ts-expect-error
          color: shapeComponentData.stroke.color,
        };

        shapeComponentData.strokes.push(solidPaintData);

        //@ts-expect-error
        shapeComponentData.strokeWidth = shapeComponentData.stroke.width;
        //@ts-expect-error
        shapeComponentData.strokeCap = shapeComponentData.stroke.cap;
        //@ts-expect-error
        shapeComponentData.strokeJoin = shapeComponentData.stroke.join;

        //@ts-expect-error
        delete shapeComponentData.stroke;
      }
    }
  }

  json.version = JSONSceneVersion['3_5'];

  return json;
}

export function version35Migration (json: JSONScene): JSONScene {
  // 处理富文本 lineGap 兼容性
  if (json.components) {
    // 遍历所有组件，处理富文本组件
    for (const component of json.components) {
      // 识别富文本组件并处理 lineGap 兼容性
      if (
        component.dataType === spec.DataType.RichTextComponent
      ) {
        const richTextComponent = component as spec.RichTextComponentData;

        if (richTextComponent.options) {
          // 检查是否已经处理过
          //@ts-expect-error
          if (richTextComponent.options.useLegacyRichText === undefined) {
            // 根据是否存在 lineGap 字段来判断版本
            if (richTextComponent.options.lineGap === undefined) {
              // 旧版本（没有 lineGap 字段）
              //@ts-expect-error
              richTextComponent.options.useLegacyRichText = true;
            } else {
              // 新版本（有 lineGap 字段）
              //@ts-expect-error
              richTextComponent.options.useLegacyRichText = false;
            }
          }
        }
      }
      // 识别富文本组件并处理 textVerticalAlign 兼容性
      if (
        component.dataType === spec.DataType.TextComponent ||
        (
          component.dataType === spec.DataType.RichTextComponent &&
          (component as spec.RichTextComponentData).options
        )
      ) {
        ensureTextVerticalAlign((component as spec.RichTextComponentData).options);
      }
      // 处理文本颜色从 0-255 到 0-1 的转换
      if (
        component.dataType === spec.DataType.TextComponent
      ) {
        convertTextColorTo01((component as spec.TextComponentData).options);
      }
    }
  }

  json.version = JSONSceneVersion['3_6'];

  return json;
}

export function version36Migration (json: JSONScene): JSONScene {
  // 兼容老数据 text item 的 transform scale 问题，老版本 text item 的 scale 实际上是 size，新的版本中 scale 和 size 分离
  const textItemScaleInfos: { item: spec.VFXItemData, scaleFactor: number, oldScaleX: number, oldScaleY: number, oldScaleZ: number }[] = [];

  for (const textItem of json.items) {
    if (textItem.type === spec.ItemType.text && textItem.transform) {
      const textComponent = json.components.find(comp => textItem.components[0].id === comp.id) as spec.TextComponentData;

      const itemWorldPerPixel = textItem.transform.scale.x / (textComponent.options?.textWidth ?? 1);
      const defaultWorldPerPixel = 0.0123;
      const scaleFactor = itemWorldPerPixel / defaultWorldPerPixel;

      const oldScaleX = textItem.transform.scale.x;
      const oldScaleY = textItem.transform.scale.y;
      const oldScaleZ = textItem.transform.scale.z;

      for (const item of json.items) {
        if (item.parentId === textItem.id && item.transform) {
          item.transform.scale.x *= oldScaleX / scaleFactor;
          item.transform.scale.y *= oldScaleY / scaleFactor;
          item.transform.scale.z *= oldScaleZ / scaleFactor;
        }
      }

      textItem.transform.scale.x = scaleFactor;
      textItem.transform.scale.y = scaleFactor;
      textItem.transform.scale.z = scaleFactor;

      textItemScaleInfos.push({ item: textItem, scaleFactor, oldScaleX, oldScaleY, oldScaleZ });
    }
  }

  // 迁移 AnimationClip 中指向 text item 的 scaleCurves 关键帧
  if (textItemScaleInfos.length > 0 && json.animations?.length) {
    migrateTextScaleCurves(json, textItemScaleInfos);
  }

  // 生成 item 的 children 字段，CompositionComponent 的 children 字段
  const itemMap = new Map<string, spec.VFXItemData>();
  const compositionMap = new Map<string, spec.CompositionData>();
  const componentMap = new Map<string, spec.ComponentData>();

  for (const component of json.components) {
    componentMap.set(component.id, component);
  }

  for (const item of json.items) {
    item.children = [];
    itemMap.set(item.id, item);
  }

  for (const composition of json.compositions) {
    composition.children = [];

    for (const componentDataPath of composition.components) {
      const componentData = componentMap.get(componentDataPath.id) as spec.ComponentData;

      if (componentData.dataType === spec.DataType.CompositionComponent) {
        const compositionComponent = componentData as spec.CompositionComponentData;

        for (const itemPath of compositionComponent.items) {
          const item = itemMap.get(itemPath.id) as spec.VFXItemData;

          if (item.parentId === undefined) {
            composition.children.push({ id: item.id });
          }
        }

        //@ts-expect-error
        delete compositionComponent.items;
      }
    }

    compositionMap.set(composition.id, composition);
  }

  for (const item of json.items) {
    if (item.parentId) {
      const parentItem = itemMap.get(item.parentId);

      if (parentItem) {
        //@ts-expect-error
        parentItem.children.push({ id: item.id });
      }
    }
  }

  json.version = JSONSceneVersion['3_7'];

  return json;
}

/**
 * 确保文本组件有版本标识字段
 */
function ensureTextVerticalAlign (options: any) {
  // 检查是否已经处理过
  if (!options || options.TextVerticalAlign !== undefined) {
    return;
  }

  // 根据是否存在TextVerticalAlign字段来判断版本
  if (options.TextVerticalAlign === undefined) {
    //旧版本（没有 TextVerticalAlign 字段）
    options.TextVerticalAlign = options.textBaseline;
  }
}

/**
 * 将文本颜色从 0-255 转换到 0-1
 */
function convertTextColorTo01 (options: spec.TextContentOptions) {
  if (!options || !options.textColor) {
    return;
  }

  const textColor = options.textColor;

  // 将 RGB 从 0-255 转换到 0-1（alpha 通道已经是 0-1，不需要转换）
  options.textColor = [
    textColor[0] / 255.0,
    textColor[1] / 255.0,
    textColor[2] / 255.0,
    textColor[3] ?? 1, // alpha 保持不变
  ];
}

/**
 * 根据形状获取形状几何体数据
 * @param shape - 形状
 * @returns 形状几何体数据
 */
function createGeometryDataByShape (shape: spec.ShapeGeometry, geometryDataName = '形状') {
  const targetGeometry = getGeometryByShape(shape);
  const { index = [], aPoint = [] } = targetGeometry;
  const point = new Float32Array(aPoint);
  const position = [];
  const atlasOffset = [];

  for (let i = 0; i < point.length; i += 6) {
    atlasOffset.push(aPoint[i + 2], aPoint[i + 3]);
    position.push(point[i], point[i + 1], 0.0);
  }

  // 用 position altasOffset index 创建GeometryData
  const subMeshes: {
    offset: number,
    indexCount: number,
    vertexCount: number,
  }[] = [];
  const vertexCount = position.length / 3;
  const indexCount = index.length;
  const positionByteLength = position.length * 4;
  const uvByteLength = atlasOffset.length * 4;
  const vertexByteLength = positionByteLength + uvByteLength;
  const indexByteLength = index.length * 2;

  const geometryData: spec.GeometryData = {
    mode: spec.GeometryType.TRIANGLES,
    vertexData: {
      vertexCount,
      channels: [],
    },
    name: geometryDataName,
    indexFormat: spec.IndexFormatType.UInt16,
    indexOffset: vertexByteLength,
    buffer: '',
    id: generateGUID(),
    dataType: spec.DataType.Geometry,
    subMeshes,
  };

  geometryData.vertexData.channels.push({
    semantic: spec.VertexBufferSemantic.Position,
    offset: 0,
    format: spec.VertexFormatType.Float32,
    dimension: 3,
  });

  geometryData.vertexData.channels.push({
    semantic: spec.VertexBufferSemantic.Uv,
    offset: positionByteLength,
    format: spec.VertexFormatType.Float32,
    dimension: 2,
  });

  geometryData.subMeshes.push({
    offset: 0,
    indexCount,
    vertexCount,
  });

  const supByteLength = indexByteLength % 4 === 0 ? 0 : 2;
  const infoBuffer = new ArrayBuffer(vertexByteLength + indexByteLength + supByteLength);
  const vertexArray = new Float32Array(infoBuffer);

  vertexArray.set(position, 0);
  vertexArray.set(atlasOffset, position.length);

  const indexArray = new Uint16Array(infoBuffer, vertexByteLength);

  indexArray.set(index, 0);

  const uint8View = new Uint8Array(infoBuffer).slice(0, vertexByteLength + indexByteLength);

  geometryData.binaryData = uint8View;

  return geometryData;
}

export function processContent (composition: spec.CompositionData) {
  //@ts-expect-error
  for (const item of composition.items) {
    const itemProps = itemMap.get(item.id);

    if (!itemProps) {
      return;
    }

    if (
      itemProps.type === spec.ItemType.sprite ||
      itemProps.type === spec.ItemType.particle ||
      itemProps.type === spec.ItemType.spine ||
      itemProps.type === spec.ItemType.text ||
      itemProps.type === spec.ItemType.richtext ||
      itemProps.type === spec.ItemType.video ||
      itemProps.type === spec.ItemType.shape
    ) {
      const component = componentMap.get(itemProps.components[0].id);

      if (component) {
        processMask(component);
      }
    }
  }
}

export function processMask (renderContent: any) {
  const renderer = renderContent.renderer;
  const maskMode = renderer?.maskMode;

  if (!maskMode || maskMode === MaskMode.NONE) {

    return;
  }

  if (maskMode === MaskMode.MASK) {
    renderContent.mask = {
      isMask: true,
    };
    currentMaskComponent = renderContent.id;
  } else if (
    maskMode === spec.ObscuredMode.OBSCURED ||
    maskMode === spec.ObscuredMode.REVERSE_OBSCURED
  ) {
    renderContent.mask = {
      inverted: maskMode === spec.ObscuredMode.REVERSE_OBSCURED ? true : false,
      reference: {
        'id': currentMaskComponent,
      },
    };
  }
}

export function convertParam (content?: BaseContent) {
  if (!content) {
    return;
  }
  for (const key of Object.keys(content)) {
    const value = content[key];
    const isArray = Array.isArray(value);

    if (isArray && value.length === 2 && Array.isArray(value[1])) {
      if (key === 'path') {
        content[key] = ensureFixedVec3(value);
      } else {
        content[key] = ensureFixedNumber(value);
      }
    } else if (!isArray && typeof value === 'object') {
      convertParam(value);
    }
  }
}

function convertTimelineAsset (composition: CompositionData, guidToItemMap: Record<string, Item>, jsonScene: JSONScene) {
  const sceneBindings = [];
  const trackDatas = [];
  const playableAssetDatas = [];
  const timelineAssetData: TimelineAssetData = {
    tracks: [],
    id: generateGUID(),
    //@ts-expect-error
    dataType: 'TimelineAsset',
  };

  //@ts-expect-error
  for (const itemDataPath of composition.items) {
    const item = guidToItemMap[itemDataPath.id];
    const subTrackDatas = [];

    const newActivationPlayableAsset = {
      id: generateGUID(),
      dataType: 'ActivationPlayableAsset',
    };

    playableAssetDatas.push(newActivationPlayableAsset);
    const newActivationTrackData = {
      id: generateGUID(),
      dataType: 'ActivationTrack',
      children: [],
      clips: [
        {
          start: item.delay,
          duration: item.duration,
          endBehavior: item.endBehavior,
          asset: {
            id: newActivationPlayableAsset.id,
          },
        },
      ],
    };

    subTrackDatas.push({ id: newActivationTrackData.id });
    trackDatas.push(newActivationTrackData);

    if (item.type !== ItemType.particle) {
      const newTransformPlayableAssetData = {
        id: generateGUID(),
        dataType: 'TransformPlayableAsset',
        //@ts-expect-error
        sizeOverLifetime: item.content.sizeOverLifetime,
        //@ts-expect-error
        rotationOverLifetime: item.content.rotationOverLifetime,
        //@ts-expect-error
        positionOverLifetime: item.content.positionOverLifetime,
      };

      playableAssetDatas.push(newTransformPlayableAssetData);
      const newTrackData = {
        id: generateGUID(),
        dataType: 'TransformTrack',
        children: [],
        clips: [
          {
            start: item.delay,
            duration: item.duration,
            endBehavior: item.endBehavior,
            asset: {
              id: newTransformPlayableAssetData.id,
            },
          },
        ],
      };

      subTrackDatas.push({ id: newTrackData.id });
      trackDatas.push(newTrackData);
    }

    if (item.type === ItemType.sprite || item.type === ItemType.text) {
      const newSpriteColorPlayableAssetData = {
        id: generateGUID(),
        dataType: 'SpriteColorPlayableAsset',
        colorOverLifetime: item.content.colorOverLifetime,
      };

      playableAssetDatas.push(newSpriteColorPlayableAssetData);
      const newTrackData = {
        id: generateGUID(),
        dataType: 'SpriteColorTrack',
        children: [],
        clips: [
          {
            start: item.delay,
            duration: item.duration,
            endBehavior: item.endBehavior,
            asset: {
              id: newSpriteColorPlayableAssetData.id,
            },
          },
        ],
      };

      subTrackDatas.push({ id: newTrackData.id });
      trackDatas.push(newTrackData);
    }

    if (item.type === ItemType.composition) {
      const newSubCompositionPlayableAssetData = {
        id: generateGUID(),
        dataType: 'SubCompositionPlayableAsset',
      };

      playableAssetDatas.push(newSubCompositionPlayableAssetData);
      const newTrackData = {
        id: generateGUID(),
        dataType: 'SubCompositionTrack',
        children: [],
        clips: [
          {
            start: item.delay,
            duration: item.duration,
            endBehavior: item.endBehavior,
            asset: {
              id: newSubCompositionPlayableAssetData.id,
            },
          },
        ],
      };

      subTrackDatas.push({ id: newTrackData.id });
      trackDatas.push(newTrackData);
    }

    const bindingTrackData = {
      id: generateGUID(),
      dataType: 'ObjectBindingTrack',
      children: subTrackDatas,
      clips: [],
    };

    trackDatas.push(bindingTrackData);
    timelineAssetData.tracks.push({ id: bindingTrackData.id });
    sceneBindings.push({
      key: { id: bindingTrackData.id },
      value: { id: item.id },
    });
  }

  const trackIds = [];

  for (const trackData of trackDatas) {
    trackIds.push({ id: trackData.id });
  }

  //@ts-expect-error
  composition.timelineAsset = { id: timelineAssetData.id };
  //@ts-expect-error
  composition.sceneBindings = sceneBindings;

  jsonScene.miscs.push(timelineAssetData);

  for (const trackData of trackDatas) {
    //@ts-expect-error
    jsonScene.miscs.push(trackData);
  }
  for (const playableAsset of playableAssetDatas) {
    //@ts-expect-error
    jsonScene.miscs.push(playableAsset);
  }
}

export function convertBinaryAsset (bins: BinaryFile[], jsonScene: JSONScene) {
  //@ts-expect-error
  jsonScene.bins = bins.map(bin => ({
    url: bin.url,
    'dataType': 'BinaryAsset',
    id: generateGUID(),
  }));
}

export function convertSpineData (resource: SpineResource, content: SpineContent, jsonScene: JSONScene) {
  //@ts-expect-error
  content.resource = {
    'atlas': {
      'bins': {
        //@ts-expect-error
        'id': jsonScene.bins[resource.atlas[1][0]].id,
      },
      'source': resource.atlas[1].slice(1),
    },
    'skeleton': {
      'bins': {
        //@ts-expect-error
        'id': jsonScene.bins[resource.skeleton[1][0]].id,
      },
      'source': resource.skeleton[1].slice(1),
    },
    'skeletonType': resource.skeletonType,
    'images': resource.images.map(i => ({
      //@ts-expect-error
      id: jsonScene.textures[i].id,
    })),
  };

}

/**
 * 批量迁移所有指向 textItem（或其子元素）的 scaleCurves 关键帧。
 * Animator 组件挂在 composition 上，path 从 composition 的顶层 item 开始解析。
 * 所有 Map 只构建一次，避免每个 text item 重复分配。
 */
function migrateTextScaleCurves (
  json: JSONScene,
  textItemScaleInfos: { item: spec.VFXItemData, scaleFactor: number, oldScaleX: number, oldScaleY: number, oldScaleZ: number }[],
) {
  // 建立 id → item 和 id → children 映射
  const idToItem = new Map<string, spec.VFXItemData>();
  const childrenMap = new Map<string, spec.VFXItemData[]>();

  for (const item of json.items) {
    idToItem.set(item.id, item);
    if (item.parentId) {
      let children = childrenMap.get(item.parentId);

      if (!children) {
        children = [];
        childrenMap.set(item.parentId, children);
      }
      children.push(item);
    }
  }

  // 为每个 textItem 收集子孙 id 集合，并建立 targetId → scaleInfo 的快速查找
  const textItemIdToInfo = new Map<string, typeof textItemScaleInfos[0]>();
  const textChildIdToInfo = new Map<string, typeof textItemScaleInfos[0]>();

  for (const info of textItemScaleInfos) {
    textItemIdToInfo.set(info.item.id, info);

    // 只收集直接子节点，深层后代已通过直接子节点的静态 scale 调整继承了正确的变换
    const children = childrenMap.get(info.item.id);

    if (children) {
      for (const child of children) {
        textChildIdToInfo.set(child.id, info);
      }
    }
  }

  // 建立 component id → component 映射
  const componentMap = new Map<string, spec.ComponentData>();

  for (const comp of json.components) {
    componentMap.set(comp.id, comp);
  }

  // 建立 animation id → data 映射
  const animationMap = new Map<string, spec.EffectsObjectData>();

  for (const anim of json.animations) {
    animationMap.set(anim.id, anim);
  }

  // 遍历所有 composition，找 Animator 组件并处理其 scaleCurves
  for (const composition of json.compositions) {
    let animatorComp: spec.ComponentData | undefined;
    let compositionComp: spec.ComponentData | undefined;

    for (const compRef of composition.components) {
      const comp = componentMap.get(compRef.id);

      if (comp?.dataType === spec.DataType.Animator) {
        animatorComp = comp;
      } else if (comp?.dataType === spec.DataType.CompositionComponent) {
        compositionComp = comp;
      }
      if (animatorComp && compositionComp) {
        break;
      }
    }

    if (!animatorComp) {
      continue;
    }

    // 获取 composition 的顶层 items
    let topItems: spec.VFXItemData[] | undefined;

    if (compositionComp) {
      const ccData = compositionComp as spec.CompositionComponentData;
      const items = ccData.items;

      if (items) {
        topItems = [];
        for (const ref of items) {
          const item = idToItem.get(ref.id);

          if (item && !item.parentId) {
            topItems.push(item);
          }
        }
      }
    }

    if ((!topItems || topItems.length === 0) && composition.children) {
      topItems = [];
      for (const ref of composition.children) {
        const item = idToItem.get(ref.id);

        if (item) {
          topItems.push(item);
        }
      }
    }

    if (!topItems || topItems.length === 0) {
      continue;
    }

    // 获取 Animator 关联的 AnimationClip
    const animatorData = animatorComp as spec.AnimatorData;
    const graphAsset = animationMap.get(animatorData.graphAsset?.id) as spec.AnimationGraphAssetData | undefined;

    if (!graphAsset?.graphDataSet?.resources) {
      continue;
    }

    for (const clipRef of graphAsset.graphDataSet.resources) {
      const clip = animationMap.get(clipRef.id) as spec.AnimationClipData | undefined;

      if (!clip?.scaleCurves) {
        continue;
      }

      for (const scaleCurve of clip.scaleCurves) {
        const target = resolveTargetFromTopItems(topItems, scaleCurve.path, childrenMap);

        if (!target) {
          continue;
        }

        const selfInfo = textItemIdToInfo.get(target.id);

        if (selfInfo) {
          // 曲线指向 textItem 自身：从旧 scale 空间转换到新 scale 空间
          scaleVector3CurveKeyFrames(scaleCurve.keyFrames, selfInfo.scaleFactor / selfInfo.oldScaleX, selfInfo.scaleFactor / selfInfo.oldScaleY, selfInfo.scaleFactor / selfInfo.oldScaleZ);

          continue;
        }

        const childInfo = textChildIdToInfo.get(target.id);

        if (childInfo) {
          // 曲线指向 textItem 的子元素：补偿父元素 scale 变化
          scaleVector3CurveKeyFrames(scaleCurve.keyFrames, childInfo.oldScaleX / childInfo.scaleFactor, childInfo.oldScaleY / childInfo.scaleFactor, childInfo.oldScaleZ / childInfo.scaleFactor);
        }
      }
    }
  }
}

function resolveTargetFromTopItems (
  topItems: spec.VFXItemData[],
  path: string,
  childrenMap: Map<string, spec.VFXItemData[]>,
): spec.VFXItemData | undefined {
  if (!path) {
    return undefined;
  }
  const segments = path.split('/');
  let current = topItems.find(item => item.name === segments[0]);

  for (let i = 1; i < segments.length && current; i++) {
    current = childrenMap.get(current.id)?.find(c => c.name === segments[i]);
  }

  return current;
}

function scaleVector3CurveKeyFrames (keyFrames: spec.Vector3CurveValue, factorX: number, factorY: number, factorZ: number) {
  if (!Array.isArray(keyFrames) || keyFrames[0] !== spec.ValueType.VECTOR3_CURVE) {
    return;
  }
  const value = keyFrames[1];

  if (!Array.isArray(value) || value.length !== 3) {
    return;
  }
  scaleBezierValue(value[0], factorX);
  scaleBezierValue(value[1], factorY);
  scaleBezierValue(value[2], factorZ);
}

function scaleBezierValue (bezier: spec.BezierValue, factor: number) {
  if (!Array.isArray(bezier) || bezier[0] !== spec.ValueType.BEZIER_CURVE || !Array.isArray(bezier[1])) {
    return;
  }

  const keyframes = bezier[1];

  for (const kf of keyframes) {
    if (!Array.isArray(kf) || kf.length < 2 || !Array.isArray(kf[1])) {
      continue;
    }
    const values = kf[1] as number[];

    for (let i = 1; i < values.length; i += 2) {
      values[i] *= factor;
    }
  }
}
